'use client';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DashboardWidgets {
  enrollment_trend: boolean;
  program_distribution: boolean;
  attendance_summary: boolean;
  fee_collection: boolean;
  recent_notifications: boolean;
  upcoming_schedule: boolean;
  quick_actions: boolean;
  announcements: boolean;
}

export interface UserPreferences {
  dashboard_widgets: DashboardWidgets;
  notification_frequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
  default_semester_view: 'current' | 'all' | 'upcoming' | 'past';
  display_compact_tables: boolean;
  display_show_avatars: boolean;
  display_sidebar_collapsed: boolean;
  display_date_format: 'DD MMM YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  display_items_per_page: 10 | 20 | 50 | 100;
  display_theme: 'light' | 'dark' | 'system';
  display_language: string;
  display_timezone: string;
}

export const DEFAULT_PREFS: UserPreferences = {
  dashboard_widgets: {
    enrollment_trend: true,
    program_distribution: true,
    attendance_summary: true,
    fee_collection: true,
    recent_notifications: true,
    upcoming_schedule: true,
    quick_actions: true,
    announcements: true,
  },
  notification_frequency: 'realtime',
  default_semester_view: 'current',
  display_compact_tables: false,
  display_show_avatars: true,
  display_sidebar_collapsed: false,
  display_date_format: 'DD MMM YYYY',
  display_items_per_page: 20,
  display_theme: 'system',
  display_language: 'en',
  display_timezone: 'Pacific/Port_Moresby',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Format a date string or Date object using the user's preferred format.
 */
export function formatDate(
  value: string | Date | null | undefined,
  format: UserPreferences['display_date_format'],
  timezone?: string
): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return String(value);

  const tz = timezone || 'Pacific/Port_Moresby';

  const parts = new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: tz,
  }).formatToParts(d);

  const day = parts.find(p => p.type === 'day')?.value ?? '01';
  const month = parts.find(p => p.type === 'month')?.value ?? 'Jan';
  const year = parts.find(p => p.type === 'year')?.value ?? '2026';

  // Numeric month
  const monthNum = String(d.toLocaleDateString('en-AU', { month: '2-digit', timeZone: tz })).padStart(2, '0');

  if (format === 'DD MMM YYYY') return `${day} ${month} ${year}`;
  if (format === 'MM/DD/YYYY') return `${monthNum}/${day}/${year}`;
  if (format === 'YYYY-MM-DD') return `${year}-${monthNum}-${day}`;
  return `${day} ${month} ${year}`;
}

/**
 * Map default_semester_view to a human-readable label for UI display.
 */
export function semesterViewLabel(view: UserPreferences['default_semester_view']): string {
  const map: Record<string, string> = {
    current: 'Current Semester',
    all: 'All Semesters',
    upcoming: 'Upcoming Semester',
    past: 'Past Semesters',
  };
  return map[view] ?? 'Current Semester';
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface PreferencesContextValue {
  prefs: UserPreferences;
  loading: boolean;
  /** Reload prefs from Supabase (call after saving in PreferencesContent) */
  reload: () => Promise<void>;
  /** Format a date using the user's preferred format + timezone */
  fmt: (value: string | Date | null | undefined) => string;
}

const PreferencesContext = createContext<PreferencesContextValue>({
  prefs: DEFAULT_PREFS,
  loading: true,
  reload: async () => {},
  fmt: (v) => String(v ?? '—'),
});

export function usePreferences() {
  return useContext(PreferencesContext);
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const themeApplied = useRef<string>('');

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    const supabase = createClient();
    try {
      const { data } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setPrefs({
          dashboard_widgets: { ...DEFAULT_PREFS.dashboard_widgets, ...(data.dashboard_widgets || {}) },
          notification_frequency: data.notification_frequency ?? DEFAULT_PREFS.notification_frequency,
          default_semester_view: data.default_semester_view ?? DEFAULT_PREFS.default_semester_view,
          display_compact_tables: data.display_compact_tables ?? DEFAULT_PREFS.display_compact_tables,
          display_show_avatars: data.display_show_avatars ?? DEFAULT_PREFS.display_show_avatars,
          display_sidebar_collapsed: data.display_sidebar_collapsed ?? DEFAULT_PREFS.display_sidebar_collapsed,
          display_date_format: data.display_date_format ?? DEFAULT_PREFS.display_date_format,
          display_items_per_page: data.display_items_per_page ?? DEFAULT_PREFS.display_items_per_page,
          display_theme: data.display_theme ?? DEFAULT_PREFS.display_theme,
          display_language: data.display_language ?? DEFAULT_PREFS.display_language,
          display_timezone: data.display_timezone ?? DEFAULT_PREFS.display_timezone,
        });
      }
    } catch {
      // No row yet — defaults are fine
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Apply theme to <html> element
  useEffect(() => {
    const theme = prefs.display_theme;
    if (themeApplied.current === theme) return;
    themeApplied.current = theme;

    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else if (theme === 'light') {
      root.classList.add('light');
      root.classList.remove('dark');
    } else {
      // system
      root.classList.remove('dark', 'light');
    }
  }, [prefs.display_theme]);

  const fmt = useCallback(
    (value: string | Date | null | undefined) =>
      formatDate(value, prefs.display_date_format, prefs.display_timezone),
    [prefs.display_date_format, prefs.display_timezone]
  );

  return (
    <PreferencesContext.Provider value={{ prefs, loading, reload: load, fmt }}>
      {children}
    </PreferencesContext.Provider>
  );
}
